import type { Draft } from 'immer';
import type { DesignUpdateEventHandler, LegacyPreviewIframeMessage } from '@shared/utils/previewMessageBus';
import { usePreviewListener } from '@shared/utils/previewMessageBus';
import type { SupportedEventPages } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import { getCustomSquatterPreviewPath, useEventPageRoute, useEventPageRouting } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import type { GuestEventDetailsByNameQuery } from './GuestRegistry.graphql.generated';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useHistory } from '@react-router';
import { EventPageType, PageKind } from '@graphql/generated-base';
import { isRegistryPage } from '@shared/utils/migratedCustomPages';
import { useIsNewStackEvent } from '@shared/utils/eventInfo/useIsNewStackEvent';

export type OnPreviewListenerUpdate = (cb: (eventDraft: Draft<NonNullable<GuestEventDetailsByNameQuery['eventByName']>>) => void) => void;

type PreviewListenerProps = Readonly<{
  eventHandle: string;
  pages: Maybe<EventPageFragment[]>;
  setRegistryPhoto: React.Dispatch<React.SetStateAction<string | undefined>>;
  onUpdate: OnPreviewListenerUpdate;
}>;

export const GuestRegistryPreviewListener = ({ eventHandle, pages, setRegistryPhoto, onUpdate }: PreviewListenerProps) => {
  const history = useHistory();
  const eventPageRoutes = useEventPageRouting(eventHandle, pages || []);
  // Empty pages: no page is the "default", so goToPath never collapses the selection to the root URL.
  const eventPageRoute = useEventPageRoute(eventHandle, []);
  const isNewStackEvent = useIsNewStackEvent();

  const handleDesignUpdate = useEventCallback<DesignUpdateEventHandler>(message => {
    if (message.action === 'fromWebsiteDesignerPayload') {
      onUpdate(eventDraft => {
        eventDraft.eventDesign = message.value.eventDesign;
      });
    } else if (message.action === 'fromList') {
      message.value.forEach(update => {
        if (update.key === 'currentPageType') {
          // Already showing the registry: navigating to /registrylinks would only bounce back here
          // via a full reload (useRegistryLinksRedirect). A user page named "registry" still navigates.
          const isRegistrySelection = update.pageKind !== PageKind.userCustom && isRegistryPage(update.pageType ?? (update.value as EventPageType), update.value);
          if (isRegistrySelection) {
            return;
          }
          // CORE-6456: a user page squatting a native slug lives at /page/<slug>, as in PreviewListener.
          const squatterPath = getCustomSquatterPreviewPath(eventHandle, update.pageType, update.value, pages || [], update.pageKind, isNewStackEvent);
          if (squatterPath) {
            history.push({ pathname: squatterPath, search: history.location.search });
            return;
          }
          const route = eventPageRoutes[update.value as SupportedEventPages];
          if (route) {
            history.push({ pathname: route.path });
          } else if (update.value) {
            // Custom pages arrive as slugs, which the page-type routes above never match; without this
            // a guest-nav "Registry" click leaves the preview stuck here. goToPath keeps the preview query.
            history.push(eventPageRoute.goToPath(EventPageType.custom, update.value));
          }
        } else {
          _updateFromLegacyPreviewMessage(setRegistryPhoto, update);
        }
      });
    }
  });

  // Subscribe to preview updates
  usePreviewListener({ onDesignUpdate: handleDesignUpdate });

  // This component is used for side effects, not rendering.
  return null;
};

const _updateFromLegacyPreviewMessage = (setRegistryPhoto: PreviewListenerProps['setRegistryPhoto'], update: LegacyPreviewIframeMessage): void => {
  switch (update.key) {
    case 'coverPhoto': {
      const { asset } = update.value;
      setRegistryPhoto(asset?.url);
      return;
    }
  }
};
